<template>
  <div class="leg-card card">
    <button
      class="leg-card__button"
      @click="handleClick"
      :class="{ 'leg-card__button--disabled': !hasSeatReservation }"
      :disabled="!hasSeatReservation"
    >
      <div class="leg-card__header">
        <div class="leg-card__header-top">
          {{ $t('travelPlanner.seat.transfer.segement') }}
          <div class="leg-card__schedule">
            {{ formatDate(leg.service_schedule_date) }} | {{ departureTime }} -
            {{ arrivalTime }}
          </div>
        </div>
        <div class="leg-card__stations">
          {{ leg.departure_station.name }}
          <svg class="leg-card__stations-icon">
            <use :xlink:href="`#${arrow}`" />
          </svg>
          {{ leg.arrival_station.name }}
        </div>
      </div>
      <div class="leg-card__body">
        <div class="leg-card__seats">
          <template v-if="hasLayoutError || !hasSeatReservation">
            <p class="body-medium">
              {{ $t('travelPlanner.seat.noSeatReservation') }}
            </p>
          </template>
          <template v-else>
            <template v-if="selectedSeats?.length">
              <div
                v-for="seat in selectedSeats"
                :key="seat.seat_number"
              >
                {{ $t('travelPlanner.seat.seat') }} {{ seat.seat_number }}
              </div>
            </template>
            <template v-else>
              {{ $t('travelPlanner.seat.noSeatsSelected') }}
            </template>
          </template>
        </div>
        <div
          v-if="!hasLayoutError || hasSeatReservation"
          class="leg-card__actions"
        >
          {{ $t('travelPlanner.seat.selectSeats') }}
          <svg class="leg-card__icon">
            <use :xlink:href="`#${chevron}`" />
          </svg>
        </div>
      </div>
    </button>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType, computed } from 'vue'
  import type { Leg, Journey } from '@/types/journeyTypes'
  import type { CarriageSeat } from '@/types/seatTypes'
  import { formatTime } from '@/utils/dateUtils'
  import arrow from '@/assets/icons/arrow.svg'
  import chevron from '@/assets/icons/chevron.svg'
  import { useSeatReservationStore } from '@/stores/seatReservationStore'

  export default defineComponent({
    name: 'LegCard',
    props: {
      leg: {
        type: Object as PropType<Leg>,
        required: true,
      },
      journey: {
        type: Object as PropType<Journey>,
        required: true,
      },
      direction: {
        type: String as PropType<'outbound' | 'inbound'>,
        required: true,
      },
      selectedSeats: {
        type: Array as PropType<CarriageSeat[]>,
        default: () => [],
      },
      seatChangePrice: {
        type: Number,
        required: false,
      },
      formatDate: {
        type: Function as PropType<(date: string) => string>,
        required: true,
      },
      formatPrice: {
        type: Function as PropType<(price: number) => string>,
        required: true,
      },
      openSeatSelection: {
        type: Function as PropType<() => void>,
        required: true,
      },
      hasLayoutError: {
        type: Boolean,
        default: false,
      },
    },
    setup(props) {
      const seatStore = useSeatReservationStore()

      const segmentForLeg = computed(() => {
        const segments =
          props.direction === 'outbound'
            ? props.journey?.outbound_booking_tariff_segments
            : props.journey?.inbound_booking_tariff_segments
        const segment = segments?.find(
          (segment) =>
            segment.service_identifier === props.leg.service_identifier
        )
        return segment
      })

      const hasSeatReservation = computed(() => {
        const segment = segmentForLeg.value
        if (!segment) return false

        const canReserve = seatStore.hasSeatReservation(
          props.direction,
          segment.id
        )
        return canReserve
      })

      const handleClick = () => {
        if (hasSeatReservation.value) {
          props.openSeatSelection()
        }
      }

      const departureTime = computed(() => {
        const segment = segmentForLeg.value?.booking_journey_segments?.[0]
        return formatTime(
          segment?.departure_date_time || props.leg.departure_timestamp
        )
      })

      const arrivalTime = computed(() => {
        const segment = segmentForLeg.value?.booking_journey_segments?.[0]
        return formatTime(
          segment?.arrival_date_time || props.leg.arrival_timestamp
        )
      })

      return {
        formatTime,
        arrow,
        chevron,
        departureTime,
        arrivalTime,
        hasSeatReservation,
        handleClick,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .leg-card {
    background: $clr-white;
    border-radius: $border-radius;
    margin: 1.875rem 0;
    box-shadow: $box-shadow-card;

    &__button {
      background: none;
      border: none;
      width: 100%;
      text-align: left;
      padding: 0;
      cursor: pointer;

      &--disabled {
        cursor: not-allowed;
        opacity: 0.7;
      }

      &:disabled {
        pointer-events: none;
      }
    }

    &__header {
      background-color: $clr-support-one-300;
      border-radius: $border-radius $border-radius 0 0;
      border-bottom: 1px solid $clr-primary-50;
      padding: 1rem;

      &-top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
      }
    }

    &__stations {
      @include title-medium;
      display: flex;
      align-items: center;
      margin-bottom: 0.25rem;
      color: $clr-primary;

      &-icon {
        color: $clr-secondary;
        width: 16px;
        height: 16px;
        transform: rotate(180deg);
        margin: 0 0.5rem;
      }
    }

    &__schedule {
      color: $clr-text-disable;
      font-size: 0.875rem;
    }

    &__body {
      @include body-large;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.719rem 1rem;
      border-radius: 0 0 $border-radius $border-radius;
    }

    &__seats {
      font-size: 0.875rem;
      margin-top: 1rem;

      .body-medium {
        color: var(--color-text-secondary);
      }
    }

    &__actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.875rem;
      color: $clr-primary;
    }

    &__icon {
      width: 16px;
      height: 16px;
      margin-top: 0.2rem;
      margin-left: 0.5rem;
      transform: rotate(90deg);
    }
  }
</style>
